import { X, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface InterviewAvailabilityNotificationProps {
  pendingInvitations: Array<{
    id: string;
    job_title: string;
    organization: string;
    interview_invited_at: string;
  }>;
}

export function InterviewAvailabilityNotification({ pendingInvitations }: InterviewAvailabilityNotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const isDismissed = localStorage.getItem('interview-availability-notification-dismissed');
    
    if (pendingInvitations.length > 0 && !isDismissed) {
      setIsVisible(true);
    }
  }, [pendingInvitations]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('interview-availability-notification-dismissed', 'true');
  };
  
  const handleConfirmAvailability = () => {
    // Navigate to the first pending invitation
    const firstInvitation = pendingInvitations[0];
    if (firstInvitation) {
      navigate(`/availability/${firstInvitation.id}`);
    }
  };
  
  if (!isVisible || pendingInvitations.length === 0) return null;
  
  const firstInvitation = pendingInvitations[0];
  const hasMultiple = pendingInvitations.length > 1;
  
  return (
    <div className="mb-8 p-4 border border-border rounded-sm bg-card shadow-sm">
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 p-2 rounded-full">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-card-foreground">Interview Invitation</h3>
            <p className="text-muted-foreground text-sm mt-1">
              You've been invited to interview for <strong>{firstInvitation.job_title}</strong> at{' '}
              <strong>{firstInvitation.organization}</strong>
              {hasMultiple && ` and ${pendingInvitations.length - 1} other position${pendingInvitations.length > 2 ? 's' : ''}`}.
              Please confirm your availability.
            </p>
          </div>
        </div>
        
        <button 
          onClick={handleDismiss} 
          className="text-muted-foreground hover:text-foreground"
          aria-label="Dismiss notification"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <div className="mt-4 flex justify-end">
        <Button 
          onClick={handleConfirmAvailability}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Confirm Availability
        </Button>
      </div>
    </div>
  );
}